import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// A dedicated axios instance for the EMPLOYEE PWA (separate JWT & lifecycle from CRM)
const portalApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('eor_portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

portalApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/portal/accept-invite')) {
      localStorage.removeItem('eor_portal_token');
      localStorage.removeItem('eor_portal_employee');
      if (!window.location.pathname.startsWith('/portal/login')) {
        window.location.href = '/portal/login';
      }
    }
    return Promise.reject(err);
  }
);

export const portalAuthAPI = {
  checkInvite: (token) => portalApi.get(`/eor-portal/invite/${token}`),
  acceptInvite: (token, password) => portalApi.post('/eor-portal/accept-invite', { token, password }),
  login: (email, password) => portalApi.post('/eor-portal/login', { email, password }),
  me: () => portalApi.get('/eor-portal/me'),
  updateProfile: (data) => portalApi.patch('/eor-portal/me', data),
};

export const portalOnboardingAPI = {
  get: () => portalApi.get('/eor-portal/onboarding'),
};

export const portalDocumentsAPI = {
  list: () => portalApi.get('/eor-portal/documents'),
  upload: (docType, file, label, expiryDate) => {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    if (label) fd.append('label', label);
    if (expiryDate) fd.append('expiry_date', expiryDate);
    return portalApi.post('/eor-portal/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadUrl: (id) => `${API_BASE}/eor-portal/documents/${id}/download`,
};

export const portalPoliciesAPI = {
  list: () => portalApi.get('/eor-portal/policies'),
  acknowledge: (id, typedName) => portalApi.post(`/eor-portal/policies/${id}/acknowledge`, { typed_name: typedName, consent: true }),
};

export const portalArchiveAPI = {
  list: () => portalApi.get('/eor-portal/signed-archive'),
};

// ── Phase 2 — Attendance ─────────────────────────────────────────────────────
export const portalAttendanceAPI = {
  today: () => portalApi.get('/eor-portal/attendance/today'),
  clockIn: (geo, note) => portalApi.post('/eor-portal/attendance/clock-in', { geo, note }),
  clockOut: (note) => portalApi.post('/eor-portal/attendance/clock-out', { note }),
  breakStart: () => portalApi.post('/eor-portal/attendance/break-start'),
  breakEnd: () => portalApi.post('/eor-portal/attendance/break-end'),
  list: (fromDate, toDate) => portalApi.get('/eor-portal/attendance', { params: { from_date: fromDate, to_date: toDate } }),
};

// ── Phase 3 — Leave ──────────────────────────────────────────────────────────
export const portalLeaveAPI = {
  list: () => portalApi.get('/eor-portal/leave'),
  balance: () => portalApi.get('/eor-portal/leave/balance'),
  request: (data) => portalApi.post('/eor-portal/leave', data),
  cancel: (id) => portalApi.delete(`/eor-portal/leave/${id}`),
};

// ── Phase 4 — Equipment ──────────────────────────────────────────────────────
export const portalEquipmentAPI = {
  list: () => portalApi.get('/eor-portal/equipment'),
  acknowledge: (id) => portalApi.post(`/eor-portal/equipment/${id}/acknowledge`),
};

// ── Admin-side (uses existing crm_token) ──────────────────────────────────────
const adminApi = axios.create({
  baseURL: `${API_BASE}/crm/eor-portal`,
  headers: { 'Content-Type': 'application/json' },
});
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const adminPortalAPI = {
  invite: (data) => adminApi.post('/invite', data),
  listEmployees: (params) => adminApi.get('/employees', { params }),
  getEmployee: (id) => adminApi.get(`/employees/${id}`),
  resendInvite: (id) => adminApi.post(`/employees/${id}/resend-invite`),
  decideDocument: (docId, decision, rejectReason) =>
    adminApi.patch(`/documents/${docId}/decision`, { decision, reject_reason: rejectReason }),
  downloadDocumentUrl: (docId) => `${API_BASE}/crm/eor-portal/documents/${docId}/download`,
  listPolicies: () => adminApi.get('/policies'),
  createPolicy: (data) => adminApi.post('/policies', data),
  updatePolicy: (id, data) => adminApi.patch(`/policies/${id}`, data),
  deletePolicy: (id) => adminApi.delete(`/policies/${id}`),
  // Phase 2 — Attendance
  employeeAttendance: (id, params) => adminApi.get(`/employees/${id}/attendance`, { params }),
  attendanceToday: () => adminApi.get('/attendance/today'),
  attendanceExportUrl: (from, to, employeeId) => {
    const p = new URLSearchParams({ from_date: from, to_date: to });
    if (employeeId) p.append('employee_id', employeeId);
    return `${API_BASE}/crm/eor-portal/attendance/export?${p.toString()}`;
  },
  // Phase 3 — Leave
  listLeave: (params) => adminApi.get('/leave', { params }),
  decideLeave: (id, decision, note) => adminApi.patch(`/leave/${id}/decision`, { decision, note }),
  // Phase 4 — Equipment
  employeeEquipment: (id) => adminApi.get(`/employees/${id}/equipment`),
  assignEquipment: (empId, data) => adminApi.post(`/employees/${empId}/equipment`, data),
  updateEquipment: (id, data) => adminApi.patch(`/equipment/${id}`, data),
  deleteEquipment: (id) => adminApi.delete(`/equipment/${id}`),
};

export default portalApi;
